// components/ui/GlassTabs.tsx
'use client';

import clsx from 'clsx';

interface GlassTab<T extends string> {
  id: T;
  label: string;
  hasPendingIndicator?: boolean;
  disabled?: boolean;
  title?: string;
}

interface GlassTabsProps<T extends string> {
  tabs: ReadonlyArray<GlassTab<T>>;
  activeTab: T;
  onTabChange: (id: T) => void;
  /** 'lg' = primary/parent row, 'sm' = secondary/child row. */
  size?: 'lg' | 'sm';
  /** Padding/centering wrapper classes (the control is always centered). */
  className?: string;
}

/**
 * Solid segmented control. A plain dark track holds the tabs; the active one is a
 * lighter solid pill. Single source of truth for the property nav (parent + child)
 * and the Asset Mgmt sub-tabs so they stay visually identical. (Formerly frosted
 * glass; de-glassed app-wide to solid HEX surfaces.)
 */
export function GlassTabs<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  size = 'lg',
  className,
}: GlassTabsProps<T>) {
  const lg = size === 'lg';
  return (
    <div className={clsx('flex justify-center', className)}>
      <div
        className={clsx(
          'inline-flex items-center rounded-full border border-[#2A2A2A] bg-[#181818]',
          lg ? 'gap-1 p-1' : 'gap-0.5 p-0.5',
        )}
      >
        {tabs.map((t) => {
          const active = t.id === activeTab;
          return (
            <button
              key={t.id}
              type="button"
              disabled={t.disabled}
              title={t.title}
              onClick={() => { if (!t.disabled) onTabChange(t.id); }}
              className={clsx(
                'relative max-w-[32ch] truncate rounded-full font-medium transition-colors',
                lg ? 'px-4 py-1.5 text-[13px]' : 'px-3 py-1 text-[11.5px]',
                t.disabled
                  ? 'cursor-not-allowed text-[#4D4D4D]'
                  : active
                    ? 'bg-[#2E2E2E] text-white'
                    : 'text-[#6E6E6E] hover:text-[#B5B5B5]',
              )}
            >
              {t.hasPendingIndicator && !active && (
                <span className="absolute right-1 top-0.5 h-1.5 w-1.5 rounded-full bg-amber-300" />
              )}
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
